import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, useWindowDimensions } from 'react-native';
import * as Animatable from 'react-native-animatable';
import { useQuery } from '@tanstack/react-query';
import { MaterialIcons, FontAwesome5, Ionicons } from '@expo/vector-icons';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import Chart from '@components/dataTables/chart';
import DataChart from '@components/dataTables/DataChart';
import { Card } from '@constants/Themed';
import { H4, H5, XStack, YStack } from 'tamagui';
import moment from 'moment';

interface ProductAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

const ProductAnalytics: React.FC<ProductAnalyticsProps> = ({ dateRange }) => {
  const theme = themeSelector();
  const { width } = useWindowDimensions();
  const [selectedCategory, setSelectedCategory] = useState<'inventory' | 'suppliers' | 'quality' | 'performance'>('inventory');

  // Theme-aware styles
  const cardStyle = useMemo(() => getModernCardStyle(theme), [theme]);
  const largeCardStyle = useMemo(() => getModernCardStyle(theme, 'large'), [theme]);
  const textColor = useMemo(() => getModernTextColor(theme), [theme]);
  const secondaryTextColor = useMemo(() => getModernTextColor(theme, 'secondary'), [theme]);
  const mutedTextColor = useMemo(() => getModernTextColor(theme, 'muted'), [theme]);

  // Product Inventory Data Query
  const { data: inventoryData, isLoading: inventoryLoading } = useQuery({
    queryKey: ['productInventory'],
    queryFn: async () => {
      // Generate sample inventory data
      const sampleProducts = [
        {
          _id: '1',
          name: 'Wireless Headphones Pro',
          category: 'Electronics',
          sku: 'WHP-001',
          currentStock: 150,
          minStock: 20,
          maxStock: 500,
          unitPrice: 99.99,
          supplier: 'TechCorp Ltd',
          lastRestocked: new Date('2024-01-15'),
          status: 'in_stock'
        },
        {
          _id: '2',
          name: 'Ergonomic Office Chair',
          category: 'Furniture',
          sku: 'EOC-002',
          currentStock: 5,
          minStock: 10,
          maxStock: 50,
          unitPrice: 299.99,
          supplier: 'Furniture Plus',
          lastRestocked: new Date('2024-01-10'),
          status: 'low_stock'
        },
        {
          _id: '3',
          name: 'Smart Water Bottle',
          category: 'Lifestyle',
          sku: 'SWB-003',
          currentStock: 0,
          minStock: 25,
          maxStock: 200,
          unitPrice: 49.99,
          supplier: 'LifeStyle Co',
          lastRestocked: new Date('2024-01-05'),
          status: 'out_of_stock'
        },
        {
          _id: '4',
          name: 'Bluetooth Speaker',
          category: 'Electronics',
          sku: 'BTS-004',
          currentStock: 75,
          minStock: 15,
          maxStock: 300,
          unitPrice: 79.99,
          supplier: 'AudioTech Inc',
          lastRestocked: new Date('2024-01-20'),
          status: 'in_stock'
        },
        {
          _id: '5',
          name: 'Laptop Stand',
          category: 'Accessories',
          sku: 'LS-005',
          currentStock: 12,
          minStock: 20,
          maxStock: 100,
          unitPrice: 39.99,
          supplier: 'WorkSpace Solutions',
          lastRestocked: new Date('2024-01-12'),
          status: 'low_stock'
        }
      ];
      return sampleProducts;
    },
    staleTime: 3 * 60 * 1000,
  });

  // Bounties Data Query
  const { data: bountiesData, isLoading: bountiesLoading } = useQuery({
    queryKey: ['productBounties'],
    queryFn: async () => {
      const response = await apis.getBounties();
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Flagged Items Query
  const { data: flaggedItems, isLoading: flaggedLoading } = useQuery({
    queryKey: ['flaggedProducts'],
    queryFn: async () => {
      const response = await apis.getFlaggedItems();
      return response;
    },
    staleTime: 3 * 60 * 1000,
  });

  // Product Performance Analytics
  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: ['productPerformance'],
    queryFn: async () => {
      // Generate sample performance data
      const categories = ['Electronics', 'Furniture', 'Lifestyle', 'Accessories'];
      const performanceMetrics = categories.map(category => ({
        category,
        totalProducts: Math.floor(Math.random() * 50) + 10,
        totalValue: Math.floor(Math.random() * 100000) + 20000,
        avgTurnover: Math.floor(Math.random() * 30) + 5,
        profitMargin: Math.floor(Math.random() * 40) + 15,
        satisfaction: Math.floor(Math.random() * 30) + 70
      }));
      
      return performanceMetrics;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Category Selector
  const CategorySelector = () => (
    <View style={[cardStyle, { marginHorizontal: 20, marginBottom: 16, flexDirection: 'row' }]}>
      {[
        { key: 'inventory', label: 'Inventory', icon: 'inventory' },
        { key: 'suppliers', label: 'Suppliers', icon: 'local-shipping' },
        { key: 'quality', label: 'Quality', icon: 'verified' },
        { key: 'performance', label: 'Performance', icon: 'analytics' }
      ].map((category) => (
        <Pressable
          key={category.key}
          onPress={() => setSelectedCategory(category.key as any)}
          style={[
            styles.modernButton,
            getModernActionStyle(theme, selectedCategory === category.key ? 'blue' : 'grey'),
            { 
              flex: 1, 
              marginHorizontal: 2, 
              paddingVertical: 10,
              flexDirection: 'row',
              alignItems: 'center', 
              justifyContent: 'center' 
            }
          ]}
        >
          <MaterialIcons 
            name={category.icon as any} 
            size={16} 
            color={selectedCategory === category.key ? '#ffffff' : mutedTextColor} 
          />
          <Text style={[{
            color: selectedCategory === category.key ? '#ffffff' : mutedTextColor,
            fontSize: 12,
            fontWeight: '600',
            marginLeft: 4
          }]}>
            {category.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );

  // Product Metric Card
  const ProductMetricCard = ({ title, value, change = null, icon, color = 'blue' as 'blue' | 'green' | 'red' | 'amber' | 'grey', subtitle = '' }) => (
    <Animatable.View animation="fadeInUp" duration={600} style={[cardStyle, { flex: 1, margin: 4 }]}>
      <View style={{ padding: 16 }}>
        <View style={styles.modernRow}>
          <View style={[styles.modernActionButton, getModernActionStyle(theme, color)]}>
            {icon}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[{ color: mutedTextColor, fontSize: 12, marginBottom: 4 }]}>{title}</Text>
            <Text style={[{ color: textColor, fontSize: 24, fontWeight: '700' }]}>{value}</Text>
            {subtitle && (
              <Text style={[{ color: secondaryTextColor, fontSize: 11, marginTop: 2 }]}>{subtitle}</Text>
            )}
            {change && (
              <Text style={[{ 
                fontSize: 12, 
                color: change.startsWith('+') ? '#22c55e' : change.startsWith('-') ? '#ef4444' : mutedTextColor,
                marginTop: 4
              }]}>
                {change}
              </Text>
            )}
          </View>
        </View>
      </View>
    </Animatable.View>
  );

  // Inventory Section
  const renderInventory = () => {
    if (!inventoryData) return null;

    const products = inventoryData || [];
    const inStock = products.filter(p => p.status === 'in_stock').length;
    const lowStock = products.filter(p => p.status === 'low_stock').length;
    const outOfStock = products.filter(p => p.status === 'out_of_stock').length;
    const totalValue = products.reduce((sum, p) => sum + (p.currentStock * p.unitPrice), 0);

    return (
      <>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: 16 }}>
          <ProductMetricCard
            title="Total Products"
            value={products.length}
            change="+5 this month"
            icon={<MaterialIcons name="inventory" size={20} color="#ffffff" />}
            color="blue"
            subtitle="Active products"
          />
          <ProductMetricCard
            title="In Stock"
            value={inStock}
            change="+2"
            icon={<MaterialIcons name="check-circle" size={20} color="#ffffff" />}
            color="green"
            subtitle="Available items"
          />
          <ProductMetricCard
            title="Low Stock"
            value={lowStock}
            change="+1"
            icon={<MaterialIcons name="warning" size={20} color="#ffffff" />}
            color="amber"
            subtitle="Need restocking"
          />
          <ProductMetricCard
            title="Total Value"
            value={`$${totalValue.toLocaleString()}`}
            change="+8%"
            icon={<MaterialIcons name="attach-money" size={20} color="#ffffff" />}
            color="green"
            subtitle="Inventory value"
          />
        </View>

        <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
          <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
            Inventory Status Overview
          </Text>
          
          {[
            { status: 'In Stock', count: inStock, color: '#10b981' },
            { status: 'Low Stock', count: lowStock, color: '#f59e0b' },
            { status: 'Out of Stock', count: outOfStock, color: '#ef4444' }
          ].map((item, index) => {
            const percentage = products.length > 0 ? (item.count / products.length * 100) : 0;
            
            return (
              <View key={index} style={[styles.modernRow, { marginBottom: 12 }]}>
                <View style={[{ width: 16, height: 16, borderRadius: 8, backgroundColor: item.color, marginRight: 12 }]} />
                <Text style={[{ color: textColor, flex: 1, fontSize: 14 }]}>{item.status}</Text>
                <Text style={[{ color: secondaryTextColor, fontSize: 14, marginRight: 12 }]}>{item.count}</Text>
                <Text style={[{ color: mutedTextColor, fontSize: 12, minWidth: 40 }]}>{percentage.toFixed(1)}%</Text>
              </View>
            );
          })}
        </View>

        <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
          <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
            Product Details
          </Text>
          
          {products.map((product, index) => (
            <View key={index} style={[{ marginBottom: 12, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }]}>
              <View style={[styles.modernRow, { marginBottom: 8 }]}>
                <Text style={[{ color: textColor, fontSize: 14, fontWeight: '600', flex: 1 }]}>
                  {product.name}
                </Text>
                <View style={[styles.modernActionButton, getModernActionStyle(theme, 
                  product.status === 'in_stock' ? 'green' : 
                  product.status === 'low_stock' ? 'amber' : 'red'
                ), { paddingHorizontal: 8, paddingVertical: 4 }]}>
                  <Text style={[{ color: '#ffffff', fontSize: 10, fontWeight: '600' }]}>
                    {product.status.replace('_', ' ').toUpperCase()}
                  </Text>
                </View>
              </View>
              
              <View style={[styles.modernRow, { marginBottom: 4 }]}>
                <Text style={[{ color: secondaryTextColor, fontSize: 12, flex: 1 }]}>
                  SKU: {product.sku}
                </Text>
                <Text style={[{ color: secondaryTextColor, fontSize: 12 }]}>
                  Stock: {product.currentStock}/{product.maxStock}
                </Text>
              </View>
              
              <View style={[styles.modernRow, { marginBottom: 4 }]}>
                <Text style={[{ color: mutedTextColor, fontSize: 11, flex: 1 }]}>
                  Category: {product.category}
                </Text>
                <Text style={[{ color: mutedTextColor, fontSize: 11 }]}>
                  Price: ${product.unitPrice}
                </Text>
              </View>
              
              <Text style={[{ color: mutedTextColor, fontSize: 11 }]}>
                Supplier: {product.supplier} • Last Restocked: {moment(product.lastRestocked).fromNow()}
              </Text>
            </View>
          ))}
        </View>
      </>
    );
  };

  // Suppliers Section
  const renderSuppliers = () => {
    if (!inventoryData) return null;

    const products = inventoryData || [];
    const suppliers = [...new Set(products.map(p => p.supplier))];
    const supplierStats = suppliers.map(supplier => {
      const supplierProducts = products.filter(p => p.supplier === supplier);
      const totalValue = supplierProducts.reduce((sum, p) => sum + (p.currentStock * p.unitPrice), 0);
      const avgStock = supplierProducts.reduce((sum, p) => sum + p.currentStock, 0) / supplierProducts.length;
      
      return {
        name: supplier,
        productCount: supplierProducts.length,
        totalValue,
        avgStock: Math.round(avgStock),
        lowStockItems: supplierProducts.filter(p => p.status === 'low_stock').length
      };
    }).sort((a, b) => b.totalValue - a.totalValue);

    return (
      <>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: 16 }}>
          <ProductMetricCard
            title="Total Suppliers"
            value={suppliers.length}
            change="+1 new"
            icon={<MaterialIcons name="local-shipping" size={20} color="#ffffff" />}
            color="blue"
            subtitle="Active suppliers"
          />
          <ProductMetricCard
            title="Top Supplier"
            value={supplierStats[0]?.productCount || 0}
            icon={<MaterialIcons name="star" size={20} color="#ffffff" />}
            color="amber"
            subtitle="Products supplied"
          />
        </View>

        <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
          <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
            Supplier Performance
          </Text>
          
          {supplierStats.map((supplier, index) => (
            <View key={index} style={[{ marginBottom: 12, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }]}>
              <View style={[styles.modernRow, { marginBottom: 8 }]}>
                <Text style={[{ color: textColor, fontSize: 14, fontWeight: '600', flex: 1 }]}>
                  {supplier.name}
                </Text>
                <Text style={[{ color: secondaryTextColor, fontSize: 12 }]}>
                  {supplier.productCount} products
                </Text>
              </View>
              
              <View style={[styles.modernRow, { marginBottom: 4 }]}>
                <Text style={[{ color: mutedTextColor, fontSize: 12, flex: 1 }]}>
                  Total Value: ${supplier.totalValue.toLocaleString()}
                </Text>
                <Text style={[{ color: mutedTextColor, fontSize: 12 }]}>
                  Avg Stock: {supplier.avgStock}
                </Text>
              </View>
              
              {supplier.lowStockItems > 0 && (
                <Text style={[{ color: '#f59e0b', fontSize: 11, marginTop: 4 }]}>
                  ⚠️ {supplier.lowStockItems} items need restocking
                </Text>
              )}
            </View>
          ))}
        </View>
      </>
    );
  };

  // Quality Section
  const renderQuality = () => {
    if (!flaggedItems) return null;

    const flagged = flaggedItems || [];

    return (
      <>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: 16 }}>
          <ProductMetricCard
            title="Flagged Items"
            value={flagged.length}
            change="-2 resolved"
            icon={<MaterialIcons name="flag" size={20} color="#ffffff" />}
            color="red"
            subtitle="Need attention"
          />
          <ProductMetricCard
            title="Quality Score"
            value="94%"
            change="+2%"
            icon={<MaterialIcons name="verified" size={20} color="#ffffff" />}
            color="green"
            subtitle="Overall rating"
          />
        </View>

        {flagged.length > 0 && (
          <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
            <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
              Quality Issues
            </Text>
            
            {flagged.slice(0, 5).map((item, index) => (
              <View key={index} style={[{ marginBottom: 12, padding: 12, backgroundColor: theme ? '#fef2f2' : '#1e1b1b', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ef4444' }]}>
                <View style={[styles.modernRow, { marginBottom: 4 }]}>
                  <Text style={[{ color: textColor, fontSize: 14, fontWeight: '600', flex: 1 }]}>
                    {item.title || 'Flagged Item'}
                  </Text>
                  <MaterialIcons name="flag" size={20} color="#ef4444" />
                </View>
                <Text style={[{ color: mutedTextColor, fontSize: 12 }]}>
                  Flagged: {item.flaggedAt ? moment(item.flaggedAt).fromNow() : 'Recently'}
                </Text>
                <Text style={[{ color: secondaryTextColor, fontSize: 11, marginTop: 4 }]}>
                  Reason: {item.reason || 'Quality control issue'}
                </Text>
              </View>
            ))}
          </View>
        )}

        {bountiesData && (
          <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
            <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
              Product Bounties
            </Text>
            
            {bountiesData.slice(0, 4).map((bounty, index) => (
              <View key={index} style={[{ marginBottom: 12, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }]}>
                <View style={[styles.modernRow, { marginBottom: 4 }]}>
                  <Text style={[{ color: textColor, fontSize: 14, fontWeight: '600', flex: 1 }]}>
                    {bounty.title || 'Product Bounty'}
                  </Text>
                  <View style={[styles.modernActionButton, getModernActionStyle(theme, 'green'), { paddingHorizontal: 8, paddingVertical: 4 }]}>
                    <Text style={[{ color: '#ffffff', fontSize: 12, fontWeight: '600' }]}>
                      ${bounty.amount || 0}
                    </Text>
                  </View>
                </View>
                <Text style={[{ color: mutedTextColor, fontSize: 12 }]}>
                  Status: {bounty.status || 'Open'} • Created: {bounty.createdAt ? moment(bounty.createdAt).fromNow() : 'Recently'}
                </Text>
              </View>
            ))}
          </View>
        )}
      </>
    );
  };

  // Performance Section
  const renderPerformance = () => {
    if (!performanceData) return null;

    const categories = performanceData || [];
    const totalProducts = categories.reduce((sum, c) => sum + c.totalProducts, 0);
    const totalValue = categories.reduce((sum, c) => sum + c.totalValue, 0);
    const avgMargin = categories.reduce((sum, c) => sum + c.profitMargin, 0) / categories.length;

    return (
      <>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: 16 }}>
          <ProductMetricCard
            title="Total Products"
            value={totalProducts}
            change="+12%"
            icon={<MaterialIcons name="inventory" size={20} color="#ffffff" />}
            color="blue"
            subtitle="All categories"
          />
          <ProductMetricCard
            title="Total Value"
            value={`$${totalValue.toLocaleString()}`}
            change="+15%"
            icon={<MaterialIcons name="attach-money" size={20} color="#ffffff" />}
            color="green"
            subtitle="Portfolio value"
          />
          <ProductMetricCard
            title="Avg Margin"
            value={`${Math.round(avgMargin)}%`}
            change="+3%"
            icon={<MaterialIcons name="trending-up" size={20} color="#ffffff" />}
            color="amber"
            subtitle="Profit margin"
          />
          <ProductMetricCard
            title="Categories"
            value={categories.length}
            icon={<MaterialIcons name="category" size={20} color="#ffffff" />}
            color="blue"
            subtitle="Product types"
          />
        </View>

        <View style={[largeCardStyle, { marginHorizontal: 20, marginTop: 16 }]}>
          <Text style={[{ color: textColor, fontSize: 16, fontWeight: '600', marginBottom: 16 }]}>
            Category Performance
          </Text>
          
          {categories.map((category, index) => (
            <View key={index} style={[{ marginBottom: 12, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }]}>
              <View style={[styles.modernRow, { marginBottom: 8 }]}>
                <Text style={[{ color: textColor, fontSize: 14, fontWeight: '600', flex: 1 }]}>
                  {category.category}
                </Text>
                <Text style={[{ color: secondaryTextColor, fontSize: 12 }]}>
                  {category.totalProducts} products
                </Text>
              </View>
              
              <View style={[styles.modernRow, { marginBottom: 4 }]}>
                <Text style={[{ color: mutedTextColor, fontSize: 12, flex: 1 }]}>
                  Value: ${category.totalValue.toLocaleString()}
                </Text>
                <Text style={[{ color: mutedTextColor, fontSize: 12 }]}>
                  Margin: {category.profitMargin}%
                </Text>
              </View>
              
              <View style={[styles.modernRow]}>
                <Text style={[{ color: mutedTextColor, fontSize: 11, flex: 1 }]}>
                  Turnover: {category.avgTurnover} days
                </Text>
                <Text style={[{ color: mutedTextColor, fontSize: 11 }]}>
                  Satisfaction: {category.satisfaction}%
                </Text>
              </View>
            </View>
          ))}
        </View>
      </>
    );
  };

  return (
    <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
      <CategorySelector />
      
      <Animatable.View animation="fadeIn" duration={400}>
        {selectedCategory === 'inventory' && renderInventory()}
        {selectedCategory === 'suppliers' && renderSuppliers()}
        {selectedCategory === 'quality' && renderQuality()}
        {selectedCategory === 'performance' && renderPerformance()}
      </Animatable.View>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
};

export default ProductAnalytics;
